import {
  DesignToken,
  ThemeName,
} from '@design/design-tokens/themes/theme-names';
import { getThemeColor } from '@design/design-tokens/themes/theme-utils';
import { hexToRgba } from '@design/utils/color-conversion/color-conversion';
import { formatPercentage } from '@helpers/utils/numbers/numbers';

import type { PieChartData } from './PieChart';

export const defaultFormatLegendValue = (value: number) =>
  formatPercentage(value, { suffix: '%' });

/**
 * Converts weights (e.g. raw counts) into whole percentages that always sum
 * to 100, using the largest-remainder method. Rounding each share on its own
 * can show 99% or 101% in total (40/13/13 -> 61/20/20).
 *
 * Pass integer counts rather than pre-normalised fractions: the floor and
 * remainder are then exact integer math, with no float drift.
 */
export const roundPercentages = (weights: number[]) => {
  const total = weights.reduce((sum, weight) => sum + weight, 0);

  if (total <= 0) {
    return weights.map(() => 0);
  }

  const percentages = weights.map(weight => Math.floor((weight * 100) / total));
  const pointsToAdd =
    100 - percentages.reduce((sum, percentage) => sum + percentage, 0);

  if (pointsToAdd === 0) {
    return percentages;
  }

  weights
    .map((weight, index) => ({ index, remainder: (weight * 100) % total }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
    .slice(0, pointsToAdd)
    .forEach(({ index }) => {
      percentages[index] += 1;
    });

  return percentages;
};

export const getColors = (theme: ThemeName) => {
  const normalColors = Array.from({ length: 5 }, (_, i) =>
    getThemeColor(`--chart-multi-lines-hex-${i + 1}` as DesignToken, theme)
  );

  return normalColors;
};

export const createPieSeries = (
  data: PieChartData,
  theme: ThemeName,
  {
    solid = false,
    disableHover = false,
  }: { solid?: boolean; disableHover?: boolean } = {}
) => {
  const normalColors = getColors(theme);
  return [
    {
      top: 'top',
      center: ['50%', '48%'],
      type: 'pie',
      silent: disableHover,
      radius: solid ? '100%' : ['60%', '95%'],
      label: { show: false },
      itemStyle: {
        borderColor: getThemeColor('--border-soft', theme),
        borderWidth: 1,
      },
      emphasis: {
        focus: 'self',
        scale: false,
      },
      blur: {
        itemStyle: { opacity: 1 },
      },
      labelLine: { show: false },
      data: data.map(({ color: colorOverride, ...item }, idx) => {
        const color = colorOverride
          ? getThemeColor(colorOverride, theme)
          : normalColors[idx % normalColors.length];

        return {
          ...item,
          itemStyle: {
            color,
          },
          emphasis: {
            itemStyle: {
              color,
            },
          },
          blur: {
            itemStyle: {
              color: hexToRgba({ hex: color, alpha: 0.15 }),
            },
          },
        };
      }),
    },
  ];
};
